import React, { useMemo } from 'react';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig 
} from '../ui/chart';
import { Download, TrendingUp, TrendingDown, Minus, ArrowUpCircle, ArrowDownCircle, Wallet } from 'lucide-react';
import { DailyCashFlow, CASH_FLOW_COLORS, CASH_FLOW_LABELS } from '../../types/cash-flow';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface CashFlowChartProps {
  data: DailyCashFlow[];
  height?: number;
  showProjections?: boolean;
  onDateClick?: (date: Date) => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

export function CashFlowChart({ 
  data, 
  height = 400, 
  showProjections = false,
  onDateClick,
  loading = false,
  title = "Fluxo de Caixa Diário",
  description = "Visualização detalhada do movimento financeiro"
}: CashFlowChartProps) {
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return data.map(item => ({
      date: new Intl.DateTimeFormat('pt-BR', { 
        day: '2-digit', 
        month: '2-digit' 
      }).format(item.date),
      fullDate: item.date,
      recebimentos: item.recebimentos,
      pagamentos: -Math.abs(item.pagamentos), // Negative for visual distinction
      transferencias_entrada: item.transferencias_entrada,
      transferencias_saida: -Math.abs(item.transferencias_saida), // Negative for visual distinction
      saldo: item.saldo_final,
      saldo_projetado: showProjections ? item.saldo_projetado : undefined,
      // Net values for better visualization
      entradas_liquidas: item.recebimentos + item.transferencias_entrada,
      saidas_liquidas: -(item.pagamentos + item.transferencias_saida)
    }));
  }, [data, showProjections]);

  // Calculate summary stats
  const summary = useMemo(() => {
    if (!data.length) return null;
    
    const totalRecebimentos = data.reduce((sum, item) => sum + item.recebimentos, 0);
    const totalPagamentos = data.reduce((sum, item) => sum + item.pagamentos, 0);
    const saldoInicial = data[0]?.saldo_inicial || 0;
    const saldoFinal = data[data.length - 1]?.saldo_final || 0;
    const variacao = saldoFinal - saldoInicial;
    
    return {
      totalRecebimentos,
      totalPagamentos,
      saldoInicial,
      saldoFinal,
      variacao,
      tendencia: variacao > 0 ? 'positiva' : variacao < 0 ? 'negativa' : 'estavel'
    };
  }, [data]);

  const formatCurrencyShort = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(value));
  };

  const formatCurrencyWithSign = (value: number) => {
    const formatted = formatCurrencyShort(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  // Chart configuration for shadcn/ui Chart components
  const chartConfig: ChartConfig = {
    recebimentos: {
      label: CASH_FLOW_LABELS.recebimentos,
      color: 'hsl(var(--finance-green))',
    },
    pagamentos: {
      label: CASH_FLOW_LABELS.pagamentos,
      color: 'hsl(var(--finance-red))',
    },
    transferencias_entrada: {
      label: CASH_FLOW_LABELS.transferencias_entrada,
      color: 'hsl(var(--finance-blue))',
    },
    transferencias_saida: {
      label: CASH_FLOW_LABELS.transferencias_saida,
      color: 'hsl(var(--finance-amber))',
    },
    saldo: {
      label: CASH_FLOW_LABELS.saldo,
      color: 'hsl(var(--finance-purple))',
    },
    saldo_projetado: {
      label: 'Saldo Projetado',
      color: 'hsl(var(--finance-teal))',
    },
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-xl p-4 min-w-[280px] animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/50">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
          <h4 className="font-semibold text-sm text-foreground">{label}</h4>
        </div>
        <div className="space-y-2 text-xs">
          {data.recebimentos > 0 && (
            <div className="flex justify-between items-center p-2 rounded-md bg-finance-green/10 border border-finance-green/20">
              <span className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-finance-green"></div>
                <span className="font-medium text-foreground">Recebimentos</span>
              </span>
              <span className="font-bold text-finance-green">
                {formatCurrencyShort(data.recebimentos)}
              </span>
            </div>
          )}
          
          {Math.abs(data.pagamentos) > 0 && (
            <div className="flex justify-between items-center p-2 rounded-md bg-finance-red/10 border border-finance-red/20">
              <span className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-finance-red"></div>
                <span className="font-medium text-foreground">Pagamentos</span>
              </span>
              <span className="font-bold text-finance-red">
                {formatCurrencyShort(Math.abs(data.pagamentos))}
              </span>
            </div>
          )}

          {data.transferencias_entrada > 0 && (
            <div className="flex justify-between items-center p-2 rounded-md bg-finance-blue/10 border border-finance-blue/20">
              <span className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-finance-blue"></div>
                <span className="font-medium text-foreground">Transf. Entrada</span>
              </span>
              <span className="font-bold text-finance-blue">
                {formatCurrencyShort(data.transferencias_entrada)}
              </span>
            </div>
          )}

          {Math.abs(data.transferencias_saida) > 0 && (
            <div className="flex justify-between items-center p-2 rounded-md bg-finance-amber/10 border border-finance-amber/20">
              <span className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-finance-amber"></div>
                <span className="font-medium text-foreground">Transf. Saída</span>
              </span>
              <span className="font-bold text-finance-amber">
                {formatCurrencyShort(Math.abs(data.transferencias_saida))}
              </span>
            </div>
          )}
          
          <div className="border-t border-border/50 pt-2 mt-2">
            <div className="flex justify-between items-center p-2.5 rounded-md bg-muted/50 border border-border">
              <span className="font-semibold text-sm text-foreground">Saldo Final</span>
              <span className={`font-bold text-base ${
                data.saldo >= 0 ? 'text-finance-green' : 'text-finance-red'
              }`}>
                {formatCurrencyWithSign(data.saldo)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getTrendIcon = () => {
    if (!summary) return <Minus className="h-4 w-4" />;
    
    switch (summary.tendencia) {
      case 'positiva':
        return <TrendingUp className="h-4 w-4 text-finance-green" />;
      case 'negativa':
        return <TrendingDown className="h-4 w-4 text-finance-red" />;
      default:
        return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const handleBarClick = (data: any) => {
    if (onDateClick && data?.fullDate) {
      onDateClick(data.fullDate);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-96 bg-muted rounded-lg"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-96 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nenhum dado encontrado</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Não há dados de fluxo de caixa para o período selecionado. 
              Verifique os filtros ou adicione transações.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {title}
              {getTrendIcon()}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Summary Badges */}
            {summary && (
              <div className="flex items-center gap-2">
                <Badge 
                  variant="outline" 
                  className={`${summary.tendencia === 'positiva' 
                    ? 'border-finance-green text-finance-green bg-finance-green/10' 
                    : summary.tendencia === 'negativa'
                    ? 'border-finance-red text-finance-red bg-finance-red/10'
                    : 'border-muted-foreground text-muted-foreground'
                  }`}
                >
                  {formatCurrencyWithSign(summary.variacao)}
                </Badge>
              </div>
            )}
            
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="w-full"
          style={{ height: `${height}px` }}
        >
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <defs>
              <linearGradient id="recebimentosGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--finance-green))" stopOpacity={0.8}/>
                <stop offset="100%" stopColor="hsl(var(--finance-green))" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="pagamentosGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--finance-red))" stopOpacity={0.1}/>
                <stop offset="100%" stopColor="hsl(var(--finance-red))" stopOpacity={0.8}/>
              </linearGradient>
              <linearGradient id="transferenciaEntradaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--finance-blue))" stopOpacity={0.8}/>
                <stop offset="100%" stopColor="hsl(var(--finance-blue))" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="transferenciaSaidaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--finance-amber))" stopOpacity={0.1}/>
                <stop offset="100%" stopColor="hsl(var(--finance-amber))" stopOpacity={0.8}/>
              </linearGradient>
              <linearGradient id="saldoGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="hsl(var(--finance-purple))" stopOpacity={0.8}/>
                <stop offset="100%" stopColor="hsl(var(--finance-teal))" stopOpacity={0.8}/>
              </linearGradient>
              <filter id="chartGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <filter id="chartShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.2" floodColor="hsl(var(--foreground))"/>
              </filter>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false}
              className="stroke-border/30"
            />
            
            <XAxis 
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickClassName="fill-muted-foreground text-xs"
              label={{ 
                value: 'Data', 
                position: 'insideBottom', 
                offset: -10,
                className: 'fill-muted-foreground text-xs font-medium'
              }}
            />
            
            <YAxis 
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => formatCurrencyShort(value)}
              tickClassName="fill-muted-foreground text-xs"
            />
            
            <ReferenceLine 
              y={0} 
              stroke="hsl(var(--border))" 
              strokeDasharray="4 4" 
              strokeWidth={1.5}
              strokeOpacity={0.5}
            />
            
            <ChartTooltip 
              content={<CustomTooltip />}
              cursor={{ 
                stroke: 'hsl(var(--border))',
                strokeWidth: 1,
                strokeDasharray: '5 5',
                opacity: 0.3
              }}
            />
            
            <ChartLegend 
              content={<ChartLegendContent />}
              wrapperStyle={{ paddingTop: '24px' }}
            />

            {/* Bars with gradients */}
            <Bar 
              dataKey="recebimentos"
              fill="url(#recebimentosGradient)"
              radius={[4, 4, 0, 0]}
              onClick={handleBarClick}
              style={{ 
                cursor: onDateClick ? 'pointer' : 'default',
                filter: 'url(#chartShadow)'
              }}
            />
            
            <Bar 
              dataKey="pagamentos"
              fill="url(#pagamentosGradient)"
              radius={[0, 0, 4, 4]}
              onClick={handleBarClick}
              style={{ 
                cursor: onDateClick ? 'pointer' : 'default',
                filter: 'url(#chartShadow)'
              }}
            />

            <Bar 
              dataKey="transferencias_entrada"
              fill="url(#transferenciaEntradaGradient)"
              radius={[4, 4, 0, 0]}
              onClick={handleBarClick}
              style={{ 
                cursor: onDateClick ? 'pointer' : 'default',
                filter: 'url(#chartShadow)'
              }}
            />

            <Bar 
              dataKey="transferencias_saida"
              fill="url(#transferenciaSaidaGradient)"
              radius={[0, 0, 4, 4]}
              onClick={handleBarClick}
              style={{ 
                cursor: onDateClick ? 'pointer' : 'default',
                filter: 'url(#chartShadow)'
              }}
            />

            {/* Balance line with gradient */}
            <Line 
              type="monotone" 
              dataKey="saldo"
              stroke="url(#saldoGradient)"
              strokeWidth={3}
              dot={{ 
                fill: 'hsl(var(--finance-purple))', 
                strokeWidth: 2, 
                r: 4,
                filter: 'url(#chartGlow)'
              }}
              activeDot={{ 
                r: 6, 
                stroke: 'hsl(var(--finance-purple))',
                strokeWidth: 2,
                fill: 'hsl(var(--background))',
                filter: 'url(#chartGlow)'
              }}
              style={{ filter: 'url(#chartGlow)' }}
            />

            {/* Projected balance line */}
            {showProjections && (
              <Line 
                type="monotone" 
                dataKey="saldo_projetado"
                stroke="hsl(var(--finance-teal))"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={false}
                strokeOpacity={0.7}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ChartContainer>
        
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 pt-6 border-t border-border/50">
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <ArrowUpCircle className="h-4 w-4 text-finance-green" />
                  Total Recebimentos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-finance-green">
                  {formatCurrency(summary.totalRecebimentos)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <ArrowDownCircle className="h-4 w-4 text-finance-red" />
                  Total Pagamentos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-finance-red">
                  {formatCurrency(summary.totalPagamentos)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  Saldo Inicial
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  summary.saldoInicial >= 0 ? 'text-finance-green' : 'text-finance-red'
                }`}>
                  {formatCurrencyWithSign(summary.saldoInicial)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className={`h-4 w-4 ${
                    summary.saldoFinal >= 0 ? 'text-finance-green' : 'text-finance-red'
                  }`} />
                  Saldo Final
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${
                  summary.saldoFinal >= 0 ? 'text-finance-green' : 'text-finance-red'
                }`}>
                  {formatCurrencyWithSign(summary.saldoFinal)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
}